import { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faCheck, faPrint, faTimes } from "@fortawesome/free-solid-svg-icons";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { FileRow } from "../../Functions/htLearningProgram";
import "./ManeuverFile.css";

// Execution state of one program step, as reflected on the sheet: the hour the
// order was given ("Dispus execut.") and the hour the trainee confirmed it
// ("Confirm execut.").
export interface RowState { dispatchedAt?: string; confirmedAt?: string; done?: boolean; }

export interface ManeuverFileProps {
    rows: FileRow[];
    theme: string;
    purpose: string;
    initialNote: string;
    sessionIdx: number | string;
    states: Record<string, RowState>;
    activeStepId?: string | null;
    accepted: boolean;
    onAccept: () => void;
    onClose: () => void;
    en?: boolean;
}

// Rows per A4 sheet. Sized for the current type scale: a row whose text wraps
// onto two lines is ~8cqw tall, so 13 of them plus the two header bands fill a
// full sheet without spilling past its (hidden) overflow. Sheet 1 also carries
// the cover block, which leaves room for far fewer rows — the maneuver table
// still starts there rather than on a sheet of its own.
const ROWS_PER_PAGE = 13;
const ROWS_ON_COVER = 5;

const T = (en: boolean | undefined, ro: string, eng: string) => (en ? eng : ro);

const TableHead = ({ en } : { en?: boolean }) => (
    <div className="mf-row mf-head">
        <div className="mf-c mf-c-time">{T(en, "Dispus execut.", "Ordered exec.")}</div>
        <div className="mf-c mf-c-time">{T(en, "Confirm execut.", "Exec. confirmed")}</div>
        <div className="mf-c mf-c-no">{T(en, "Nr. crt.", "No.")}</div>
        <div className="mf-c mf-c-text">{T(en, "Succesiunea operațiilor", "Sequence of operations")}</div>
    </div>
);

const ManeuverFile = ({
    rows, theme, purpose, initialNote, sessionIdx, states, activeStepId,
    accepted, onAccept, onClose, en,
} : ManeuverFileProps) => {
    // Sheet 0 carries the cover plus the first ROWS_ON_COVER rows; every sheet
    // after it is a full table page.
    const pages = useMemo(() => {
        const chunks: FileRow[][] = [rows.slice(0, ROWS_ON_COVER)];
        for (let i = ROWS_ON_COVER; i < rows.length; i += ROWS_PER_PAGE) chunks.push(rows.slice(i, i + ROWS_PER_PAGE));
        // The acceptance block goes under the last table, and a sheet whose rows
        // already reach the bottom has no room for it (the sheet clips). Two
        // rows' worth of room short of full, it gets a sheet of its own.
        const last = chunks[chunks.length - 1];
        const limit = chunks.length === 1 ? ROWS_ON_COVER : ROWS_PER_PAGE;
        if (last.length > limit - 2) chunks.push([]);
        return chunks;
    }, [rows]);
    const totalPages = pages.length;
    // The trainee signs the sheet off on the last page: the acceptance covers
    // the whole maneuver, so it has to sit under the end of the table.
    const acceptPage = totalPages - 1;

    const [page, setPage] = useState(0);
    const bookRef = useRef<HTMLDivElement>(null);

    // Which sheet a given row sits on.
    const pageOfRow = (idx: number) =>
        (idx < ROWS_ON_COVER ? 0 : Math.floor((idx - ROWS_ON_COVER) / ROWS_PER_PAGE) + 1);

    // Follow the dictation: whenever the active row moves onto another sheet,
    // turn to it (so the trainee always sees the row being executed).
    useEffect(() => {
        if (!activeStepId) return;
        const idx = rows.findIndex((r) => (r.rowId || r.stepId) === activeStepId);
        if (idx < 0) return;
        setPage(pageOfRow(idx));
    }, [activeStepId, rows]);

    const date = new Date();
    const p2 = (n: number) => String(n).padStart(2, "0");
    const today = `${p2(date.getDate())}.${p2(date.getMonth() + 1)}.${date.getFullYear()}`;

    const printFile = async () => {
        const sheets = bookRef.current?.querySelectorAll<HTMLElement>(".mf-sheet");
        if (!sheets?.length) return;
        const pdf = new jsPDF("p", "mm", "a4");
        for (let i = 0; i < sheets.length; i++) {
            const sheet = sheets[i];
            // Every sheet is rendered (only the current one is visible), so make
            // the off-screen ones printable for the duration of the capture.
            sheet.classList.add("mf-printing");
            const canvas = await html2canvas(sheet, { scale: 2, backgroundColor: "#ffffff" });
            sheet.classList.remove("mf-printing");
            if (i > 0) pdf.addPage();
            pdf.addImage(canvas.toDataURL("image/png"), "png", 0, 0, 210, 297);
        }
        pdf.save(T(en, "Foaie_de_manevra.pdf", "Maneuver_file.pdf"));
    };

    // Clicking the backdrop closes the sheet, the same as the Close button — so
    // only once it has been accepted, and only when the click starts and ends on
    // the backdrop itself (a text selection dragged off a sheet must not close).
    const onBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!accepted || e.target !== e.currentTarget) return;
        onClose();
    };

    return (
        <div className="mf-overlay" onMouseDown={onBackdropMouseDown}>
            <button type="button" className="mf-nav prev" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                <FontAwesomeIcon icon={faArrowLeft} />
            </button>

            <div className="mf-book" ref={bookRef}>
                {pages.map((chunk, pi) => (
                <div className={`mf-sheet${page === pi ? " current" : ""}`} key={pi}>
                    {/* ---- Sheet 1 opens with the cover: header, theme, withdrawal /
                         return blocks — the maneuver table starts underneath it. ---- */}
                    {pi === 0 ? (
                    <>
                    <div className="mf-cover-top">
                        <div className="mf-cover-col">
                            <p>Societatea S.A. ...................</p>
                            <p>{T(en, "Unitate", "Unit")} : .................................</p>
                            <p>{T(en, "Subunitate", "Sub-unit")} : .........................</p>
                        </div>
                        <div className="mf-cover-col">
                            <p>{T(en, "Întocmit (nume, dată, semnătură)", "Created (name, date, signature)")}</p>
                            <p className="mf-dots" />
                            <p>{T(en, "Aprobat (nume, dată, semnătură)", "Approved (name, date, signature)")}</p>
                            <p className="mf-dots" />
                            <p>{T(en, "Controlat (nume, dată, semnătură)", "Checked (name, date, signature)")}</p>
                            <p className="mf-dots" />
                        </div>
                    </div>

                    <h2 className="mf-title">
                        {T(en, "FOAIE DE MANEVRĂ DE EXECUȚIE NR.", "EXECUTION MANEUVER SHEET NO.")} {sessionIdx}
                    </h2>

                    <div className="mf-meta">
                        <p><b>{T(en, "Tema", "Theme")}:</b> {theme}</p>
                        <p><b>{T(en, "Scopul", "Purpose")}:</b> {purpose}</p>
                        <p><b>{T(en, "S.O.I.", "I.O.S.")}:</b> {initialNote}</p>
                    </div>

                    <div className="mf-blocks">
                        <div className="mf-block">
                            <p className="mf-block-title">{T(en, "A. RETRAGEREA DIN EXPLOATARE", "A. WITHDRAWAL FROM OPERATION")}</p>
                            <p>R.M.E. : ...........................</p>
                            <p>E.M. : ...............................</p>
                            <p>R.M.C. : ..........................</p>
                            <p>T.C.A. : ............................</p>
                            <p>{T(en, "Data executării", "Execution date")} : {today}</p>
                        </div>
                        <div className="mf-block">
                            <p className="mf-block-title">{T(en, "B. REVENIREA", "B. RETURN")}</p>
                            <p>R.M.E. : ...........................</p>
                            <p>E.M. : ...............................</p>
                            <p>R.M.C. : ..........................</p>
                            <p>T.C.A. : ............................</p>
                            <p>{T(en, "Data executării", "Execution date")} : {today}</p>
                        </div>
                    </div>

                    </>
                    ) : null}

                    {chunk.length ? (
                    <>
                    <div className="mf-table-title">
                        <span className="mf-table-time">{T(en, "Ora, minut", "Hour, minute")}</span>
                        <span className="mf-table-name">{T(en, "A. DESFĂȘURAREA MANEVREI", "A. EXECUTION OF THE MANEUVER")}</span>
                    </div>
                    <div className="mf-table">
                        <TableHead en={en} />
                        {chunk.map((row) => {
                            const key = row.rowId || row.stepId;
                            const st = key ? states[key] : undefined;
                            const active = !!key && key === activeStepId;
                            return (
                                <div className={`mf-row${row.header ? " mf-group" : ""}${active ? " active" : ""}${st?.done ? " done" : ""}`} key={row.number + row.text}>
                                    <div className="mf-c mf-c-time">{st?.dispatchedAt || ""}</div>
                                    <div className="mf-c mf-c-time">{st?.confirmedAt || ""}</div>
                                    <div className="mf-c mf-c-no">{row.number}</div>
                                    <div className="mf-c mf-c-text">{row.text}</div>
                                </div>
                            );
                        })}
                    </div>
                    </>
                    ) : null}

                    {/* The trainee signs the sheet off at the end of the sheet, once
                        the whole maneuver has been read. */}
                    {pi === acceptPage ? (
                        !accepted ? (
                            <div className="mf-accept">
                                <button type="button" className="mf-accept-box" onClick={onAccept} />
                                <p>{T(en, "Am înțeles și sunt de acord.", "I understand and I agree.")}</p>
                            </div>
                        ) : (
                            <p className="mf-accepted"><FontAwesomeIcon icon={faCheck} /> {T(en, "Foaie de manevră acceptată.", "Maneuver sheet accepted.")}</p>
                        )
                    ) : null}

                    <span className="mf-page-no">{pi + 1} / {totalPages}</span>
                </div>
                ))}
            </div>

            <button type="button" className="mf-nav next" disabled={page >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}>
                <FontAwesomeIcon icon={faArrowRight} />
            </button>

            <div className="mf-tools">
                <button type="button" className="mf-tool" onClick={printFile}>
                    <FontAwesomeIcon icon={faPrint} />
                    <span>{T(en, "Printează", "Print")}</span>
                </button>
                {accepted ? (
                    <button type="button" className="mf-tool close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                        <span>{T(en, "Închide foaia de manevră", "Close maneuver sheet")}</span>
                    </button>
                ) : null}
            </div>
        </div>
    );
};

export default ManeuverFile;
